import { Injectable, signal } from '@angular/core';
import { jsPDF } from 'jspdf';

export interface PdfExportOptions {
  title?: string;
  stages?: string[];
  stageText?: string;
  showProgress?: boolean;
  progressPart?: number;
  progressTotal?: number;
}

export interface ReportFooterConfig {
  pageNumber: number;
  totalPages: number;
  reportTitle?: string;
  scopeText?: string;
  periodText?: string;
  extraMeta?: string;
  pageWidthMm?: number;
  pageHeightMm?: number;
  marginX?: number;
}

interface SavedElementStyle {
  maxHeight: string;
  height: string;
  overflow: string;
  overflowY: string;
  overflowX: string;
  scrollTop: number;
  scrollLeft: number;
}

@Injectable({
  providedIn: 'root',
})
export class PdfExportService {
  readonly isExporting = signal<boolean>(false);
  readonly loaderTitle = signal<string>('Generating Executive PDF Report');
  readonly loaderStages = signal<string[]>([
    'Expanding tables to full height for complete capture…',
    'Rendering high-resolution vector layout…',
    'Slicing document into print-ready pages…',
    'Applying enterprise branding & pagination…',
    'Finalizing document download…',
  ]);
  readonly loaderStageText = signal<string>('');
  readonly showProgress = signal<boolean>(false);
  readonly progressPart = signal<number>(0);
  readonly progressTotal = signal<number>(1);

  /**
   * Starts the global PDF export interactive loader.
   */
  startExport(options?: PdfExportOptions): void {
    if (options?.title) {
      this.loaderTitle.set(options.title);
    } else {
      this.loaderTitle.set('Generating Executive PDF Report');
    }

    if (options?.stages && options.stages.length > 0) {
      this.loaderStages.set(options.stages);
    } else {
      this.loaderStages.set([
        'Expanding tables to full height for complete capture…',
        'Rendering high-resolution vector layout…',
        'Slicing document into print-ready pages…',
        'Applying enterprise branding & pagination…',
        'Finalizing document download…',
      ]);
    }

    this.loaderStageText.set(options?.stageText ?? '');
    this.showProgress.set(options?.showProgress ?? false);
    this.progressPart.set(options?.progressPart ?? 0);
    this.progressTotal.set(options?.progressTotal ?? 1);
    this.isExporting.set(true);
  }

  /**
   * Updates stage messaging and optional progress counter.
   */
  setStage(stageText: string, part?: number, total?: number): void {
    this.loaderStageText.set(stageText);
    if (part !== undefined && total !== undefined) {
      this.showProgress.set(true);
      this.progressPart.set(part);
      this.progressTotal.set(total);
    }
  }

  /**
   * Concludes the export process and hides the interactive loader overlay.
   */
  finishExport(): void {
    this.isExporting.set(false);
    this.loaderStageText.set('');
    this.showProgress.set(false);
  }

  /**
   * Traverses the target root element and temporarily expands all scrollable tables,
   * table wrappers, and overflow containers to their full natural height so that
   * html2canvas captures 100% of rows without clipping or inner scrollbars.
   *
   * Returns a restoration callback that reverts all modified elements to their exact
   * original styles and scroll positions.
   */
  expandScrollableContainers(root: HTMLElement): () => void {
    const targets = new Set<HTMLElement>();
    const savedStyles = new Map<HTMLElement, SavedElementStyle>();

    // 1. Explicit table and scrollable wrapper selectors
    const selectors = [
      '.gi-table-wrapper',
      '.gi-table-wrapper--tall',
      '.gi-table-wrapper--topusers',
      '.gi-table-scroll-container',
      '.gi-table-responsive',
      '.gi-footprint-list-wrap',
      '.lmd__table-container',
      '.p-datatable-wrapper',
      '.p-datatable-scrollable-body',
      '.p-datatable-scrollable-table',
      '.p-datatable-table',
      '.table-responsive',
      '[class*="table-wrapper"]',
      '[class*="table-container"]',
      '[class*="table-scroll"]',
      '[class*="scrollable-table"]',
    ];

    try {
      const explicitMatches = root.querySelectorAll<HTMLElement>(selectors.join(', '));
      explicitMatches.forEach((el) => targets.add(el));
    } catch {
      // Fallback if complex selector fails
    }

    // 2. Direct table and tbody elements inside root
    root.querySelectorAll<HTMLElement>('table, tbody').forEach((el) => {
      targets.add(el);
    });

    // 3. Scan all elements inside root: if an element contains table/tr or has scroll overflow, expand it
    const allDescendants = root.querySelectorAll<HTMLElement>('*');
    allDescendants.forEach((el) => {
      // Skip chart canvases, SVG graphics, tooltips, and interactive loader
      const tag = el.tagName.toLowerCase();
      if (
        tag === 'canvas' ||
        tag === 'svg' ||
        tag === 'app-interactive-loader' ||
        el.classList.contains('p-chart') ||
        el.classList.contains('p-tooltip')
      ) {
        return;
      }

      const hasTableContent = el.querySelector('table, tr, td, th, [role="row"]') !== null;
      if (hasTableContent) {
        const computed = window.getComputedStyle(el);
        const hasScroll =
          computed.overflowY === 'auto' ||
          computed.overflowY === 'scroll' ||
          computed.overflow === 'auto' ||
          computed.overflow === 'scroll';
        const hasRestrictedHeight =
          (computed.maxHeight !== 'none' && computed.maxHeight !== '') ||
          (el.scrollHeight > el.clientHeight + 4 && el.clientHeight > 0);

        if (hasScroll || hasRestrictedHeight) {
          targets.add(el);
        }
      }
    });

    // Save styles and expand each target element
    targets.forEach((el) => {
      savedStyles.set(el, {
        maxHeight: el.style.maxHeight,
        height: el.style.height,
        overflow: el.style.overflow,
        overflowY: el.style.overflowY,
        overflowX: el.style.overflowX,
        scrollTop: el.scrollTop,
        scrollLeft: el.scrollLeft,
      });

      el.style.setProperty('max-height', 'none', 'important');
      el.style.setProperty('height', 'auto', 'important');
      el.style.setProperty('overflow', 'visible', 'important');
      el.style.setProperty('overflow-y', 'visible', 'important');
      el.style.setProperty('overflow-x', 'visible', 'important');
    });

    // Return the restoration function
    return () => {
      savedStyles.forEach((saved, el) => {
        if (saved.maxHeight) {
          el.style.maxHeight = saved.maxHeight;
        } else {
          el.style.removeProperty('max-height');
        }

        if (saved.height) {
          el.style.height = saved.height;
        } else {
          el.style.removeProperty('height');
        }

        if (saved.overflow) {
          el.style.overflow = saved.overflow;
        } else {
          el.style.removeProperty('overflow');
        }

        if (saved.overflowY) {
          el.style.overflowY = saved.overflowY;
        } else {
          el.style.removeProperty('overflow-y');
        }

        if (saved.overflowX) {
          el.style.overflowX = saved.overflowX;
        } else {
          el.style.removeProperty('overflow-x');
        }

        el.scrollTop = saved.scrollTop;
        el.scrollLeft = saved.scrollLeft;
      });
    };
  }

  /**
   * Renders a clean, enterprise-grade, collision-free report footer onto an exported PDF page.
   *
   * Solves all horizontal and vertical overlapping issues by:
   * 1. Drawing a crisp horizontal divider separating content from footer metadata.
   * 2. Distributing information across two dedicated lines (Brand & Pagination on row 1, Scope & Audit on row 2).
   * 3. Placing items strictly at left and right boundaries with guaranteed clearance gaps.
   * 4. Dynamically measuring and truncating any long filter, email, or scope strings with ellipsis.
   */
  renderReportFooter(pdf: jsPDF, config: ReportFooterConfig): void {
    const pageWidthMm = config.pageWidthMm ?? 210;
    const pageHeightMm = config.pageHeightMm ?? 297;
    const marginX = config.marginX ?? 10;
    const usableWidth = pageWidthMm - marginX * 2;

    const lineY = pageHeightMm - 13;  // 284mm on standard A4
    const row1Y = pageHeightMm - 8.5; // 288.5mm on standard A4
    const row2Y = pageHeightMm - 4.5; // 292.5mm on standard A4

    // 1. Crisp horizontal divider line separating report content from footer
    pdf.setDrawColor(226, 232, 240); // slate-200
    pdf.setLineWidth(0.25);
    pdf.line(marginX, lineY, pageWidthMm - marginX, lineY);

    // 2. Primary row: Enterprise Brand (Left) & Page numbering (Right)
    const pageStr = `Page ${config.pageNumber} of ${config.totalPages}`;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(71, 85, 105); // slate-600
    const pageStrWidth = pdf.getTextWidth(pageStr);
    pdf.text(pageStr, pageWidthMm - marginX, row1Y, { align: 'right' });

    const systemBrand = 'Holcim Google Advanced Reporting System (GARS)';
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7.5);
    pdf.setTextColor(51, 65, 85); // slate-700
    const maxBrandWidth = usableWidth - pageStrWidth - 10;
    const brandToDraw = this.truncateToFit(pdf, systemBrand, maxBrandWidth);
    pdf.text(brandToDraw, marginX, row1Y);

    // 3. Secondary row: Report & Scope Context (Left) & Audit Timestamp (Right)
    const now = new Date();
    const dateFormatted = now.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    const rightMeta = config.extraMeta
      ? `${config.extraMeta} · ${dateFormatted}`
      : `Generated: ${dateFormatted} · Holcim Confidential`;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(6.8);
    pdf.setTextColor(148, 163, 184); // slate-400
    const rightMetaWidth = pdf.getTextWidth(rightMeta);
    pdf.text(rightMeta, pageWidthMm - marginX, row2Y, { align: 'right' });

    const parts: string[] = [];
    if (config.reportTitle) parts.push(config.reportTitle);
    if (config.scopeText) parts.push(config.scopeText);
    if (config.periodText) parts.push(config.periodText);
    const leftContext = parts.join(' · ') || 'Executive Telemetry & Adoption Report';

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(6.8);
    pdf.setTextColor(100, 116, 139); // slate-500
    const maxContextWidth = usableWidth - rightMetaWidth - 10;
    const contextToDraw = this.truncateToFit(pdf, leftContext, maxContextWidth);
    pdf.text(contextToDraw, marginX, row2Y);
  }

  /**
   * Safely truncates string with ellipsis if it exceeds the available width in millimeters.
   */
  private truncateToFit(pdf: jsPDF, text: string, maxWidthMm: number): string {
    if (maxWidthMm <= 0) return '';
    if (pdf.getTextWidth(text) <= maxWidthMm) {
      return text;
    }
    let truncated = text;
    while (truncated.length > 1 && pdf.getTextWidth(truncated + '…') > maxWidthMm) {
      truncated = truncated.slice(0, -1);
    }
    return (truncated.trim() + '…').trim();
  }
}
